import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  FormControlLabel,
  Switch,
  Chip,
  FormHelperText,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Close,
  Save,
  Image,
  AttachMoney,
  Delete,
  Add,
  Edit
} from '@mui/icons-material';
import menuService from '../../../services/menuService';
import uploadService from '../../../services/uploadService';
import SuccessDialog from '../dialogs/SuccessDialog';

const EditMenu = ({ open, onClose, item, refreshData }) => {
  const { t } = useTranslation();
  
  // State for dialog
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [editingVariation, setEditingVariation] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    categoryId: '',
    image: null,
    isAvailable: true,
    variations: []
  });

  // Validation state
  const [errors, setErrors] = useState({});
  
  // Image preview
  const [preview, setPreview] = useState(null);
  
  // New variation state
  const [newVariation, setNewVariation] = useState({
    name: '',
    price: ''
  });

  // Fetch categories separately - only depends on open
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await menuService.getAllCategories();
        setCategories(response);
        setCategoriesLoaded(true);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setError(t('menu.fetchCategoriesError'));
      }
    };
    
    if (open) {
      fetchCategories();
    }
  }, [open, t]);

  // Load item data to form when modal opens
  useEffect(() => {
    if (item && open) {
      console.log('📝 Loading item data:', item);
      
      setFormData({
        name: item.name || '',
        description: item.description || '',
        price: item.price ? item.price.toString() : '',
        categoryId: item.category_id ? item.category_id.toString() : '',
        image: item.image || null,
        isAvailable: item.available === 1 || item.available === true,
        variations: item.variations && Array.isArray(item.variations) 
          ? item.variations.map(v => ({
              id: v.variation_id || Date.now() + Math.random(), // Ensure unique ID
              variation_id: v.variation_id, // Keep database ID if it exists
              name: v.name,
              price: parseFloat(v.price || 0)
            }))
          : []
      });
      
      setPreview(item.image);
      console.log('Loaded variations:', item.variations);
    }
  }, [item, open]);

  // Validate category once both categories and form data have loaded
  useEffect(() => {
    if (categoriesLoaded && categories.length > 0 && formData.categoryId) {
      const categoryExists = categories.some(
        cat => cat.category_id.toString() === formData.categoryId
      );
      
      if (!categoryExists) {
        // Reset to first available category if current one doesn't exist
        setFormData(prev => ({
          ...prev,
          categoryId: categories[0].category_id.toString()
        }));
      }
    }
  }, [categories, formData.categoryId, categoriesLoaded]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle image upload
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Use uploadService for validation and conversion
        const base64Image = await uploadService.uploadMenuImage(file);
        
        setPreview(base64Image);
        setFormData(prev => ({
          ...prev,
          image: base64Image
        }));
        
        // Clear any previous errors
        setErrors(prev => ({
          ...prev,
          image: null
        }));
        
      } catch (error) {
        console.error('Image upload error:', error);
        setErrors(prev => ({
          ...prev,
          image: error.message || t('menu.imageUploadError')
        }));
      }
    }
  };
  
  // Clear uploaded image
  const handleClearImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null
    }));
    setPreview(null);
  };
  
  // Handle variation field changes
  const handleVariationChange = (e) => {
    const { name, value } = e.target;
    setNewVariation(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // ✅ FIXED: Add a new variation with proper state management and validation
  const handleAddVariation = () => {
    if (!newVariation.name || !newVariation.price) return;
    
    const priceValue = parseFloat(newVariation.price);
    if (isNaN(priceValue) || priceValue < 0) {
      setErrors(prev => ({
        ...prev,
        variation: 'Price must be a valid positive number'
      }));
      return;
    }
    
    const variationData = {
      id: editingVariation ? editingVariation.id : Date.now() + Math.random(), // Ensure unique ID
      variation_id: editingVariation ? editingVariation.variation_id : null, // Keep original variation_id if editing existing
      name: newVariation.name.trim(),
      price: priceValue
    };
    
    setFormData(prev => ({
      ...prev,
      variations: editingVariation 
        ? prev.variations.map(v => v.id === editingVariation.id ? variationData : v) // Update existing
        : [...prev.variations, variationData] // Add new
    }));
    
    // Reset form and editing state
    setNewVariation({ name: '', price: '' });
    setEditingVariation(null);
    setErrors(prev => ({ ...prev, variation: null }));
  };

  // ✅ FIXED: Remove a variation with proper ID handling
  const handleRemoveVariation = (variationId) => {
    console.log('🗑️ Removing variation with ID:', variationId);
    console.log('Current variations:', formData.variations);
    
    setFormData(prev => {
      const updatedVariations = prev.variations.filter(v => {
        // Handle both temporary IDs and database IDs
        const shouldKeep = v.id !== variationId && v.variation_id !== variationId;
        console.log(`Variation ${v.name} (id: ${v.id}, variation_id: ${v.variation_id}) - Keep: ${shouldKeep}`);
        return shouldKeep;
      });
      
      console.log('Updated variations after removal:', updatedVariations);
      return {
        ...prev,
        variations: updatedVariations
      };
    });
    
    // If we're currently editing this variation, cancel the edit
    if (editingVariation && (editingVariation.id === variationId || editingVariation.variation_id === variationId)) {
      setEditingVariation(null);
      setNewVariation({ name: '', price: '' });
    }
  };

  // ✅ FIXED: Edit a variation with proper state handling
  const handleEditVariation = (variationId) => {
    console.log('✏️ Editing variation with ID:', variationId);
    
    const variation = formData.variations.find(v => 
      v.id === variationId || v.variation_id === variationId
    );
    
    if (variation) {
      console.log('Found variation to edit:', variation);
      setEditingVariation(variation);
      setNewVariation({
        name: variation.name,
        price: variation.price.toString()
      });
    }
  };

  // ✅ FIXED: Cancel editing variation with proper restoration
  const handleCancelEditVariation = () => {
    setEditingVariation(null);
    setNewVariation({ name: '', price: '' });
    setErrors(prev => ({ ...prev, variation: null }));
  };

  // ✅ ENHANCED: Validate form with better error handling
  const validateForm = () => {
    const newErrors = {};
    
    // Basic information validation
    if (!formData.name.trim()) {
      newErrors.name = t('menu.nameRequired');
    }
    
    if (!formData.price) {
      newErrors.price = t('menu.priceRequired');
    } else {
      const priceValue = parseFloat(formData.price);
      if (isNaN(priceValue) || priceValue <= 0) {
        newErrors.price = t('menu.pricePositiveError');
      }
    }
    
    if (!formData.categoryId) {
      newErrors.categoryId = t('menu.categoryRequired');
    }
    
    // Variation validation
    if (formData.variations.length > 0) {
      const invalidVariations = formData.variations.filter(v => 
        !v.name || !v.name.trim() || isNaN(parseFloat(v.price)) || parseFloat(v.price) < 0
      );
      
      if (invalidVariations.length > 0) {
        newErrors.variations = `Invalid variations found. Please check all variation names and prices.`;
      }
      
      // Check for duplicate variation names
      const variationNames = formData.variations.map(v => v.name.toLowerCase().trim());
      const duplicateNames = variationNames.filter((name, index) => variationNames.indexOf(name) !== index);
      
      if (duplicateNames.length > 0) {
        newErrors.variations = `Duplicate variation names found: ${duplicateNames.join(', ')}`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Prepare menu item data
      const menuItemData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        category_id: parseInt(formData.categoryId),
        image: formData.image, // Send raw image data (base64 or URL)
        available: formData.isAvailable,
        variations: formData.variations.map(v => ({
          variation_id: v.variation_id || undefined, // Include database ID if exists
          name: v.name.trim(),
          price: parseFloat(v.price)
        }))
      };
      
      console.log('📤 Sending update data:', {
        ...menuItemData,
        image: menuItemData.image ? (menuItemData.image.startsWith('data:') ? '[BASE64_IMAGE]' : menuItemData.image) : null
      });

      // Update menu item
      await menuService.update(item.item_id, menuItemData);

      // Force refresh notifications
      window.dispatchEvent(new CustomEvent('force-notification-refresh'));
      
      // Show success message
      setSuccessDialogOpen(true);
      
      // Refresh the menu list
      if (refreshData) refreshData();
      
    } catch (error) {
      console.error('Error updating menu item:', error);
      setError(error.response?.data?.message || t('menu.updateError'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle success dialog close
  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    onClose(); // Close the edit dialog
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={() => !isLoading && onClose()}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('menu.editTitle')}
            </Typography>
            <IconButton onClick={onClose} disabled={isLoading}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {error && (
            <Box sx={{ 
              bgcolor: 'error.light', 
              color: 'error.main', 
              p: 2, 
              borderRadius: 1,
              mb: 3
            }}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                {t('menu.basicInformation')}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('menu.itemName')}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.categoryId}>
                  <InputLabel>{t('menu.category')}</InputLabel>
                  <Select
                    name="categoryId"
                    value={categories.length > 0 ? formData.categoryId : ""}
                    onChange={handleChange}
                    label={t('menu.category')}
                    displayEmpty
                  >
                    {categories.length === 0 && (
                      <MenuItem value="">
                        <em>{t('menu.loadingCategories')}</em>
                      </MenuItem>
                    )}
                    {categories.map(category => (
                      <MenuItem key={category.category_id} value={category.category_id.toString()}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.categoryId && (
                    <FormHelperText>{errors.categoryId}</FormHelperText>
                  )}
                </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('menu.price')}
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={!!errors.price}
                    helperText={errors.price}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoney />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isAvailable}
                        onChange={handleChange}
                        name="isAvailable"
                        color="primary"
                      />
                    }
                    label={t('menu.itemAvailable')}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label={t('menu.descriptionOptional')}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={4}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<Image />}
                      sx={{ 
                        mr: 2,
                        background: !preview 
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'transparent',
                        color: !preview ? 'white' : 'primary.main',
                        border: preview ? '1px solid' : 'none',
                        borderColor: 'primary.main',
                        '&:hover': {
                          background: !preview 
                            ? 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                            : 'rgba(102, 126, 234, 0.05)'
                        }
                      }}
                    >
                      {preview ? t('menu.changeImage') : t('menu.uploadImage')}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleImageChange}
                      />
                    </Button>
                    
                    {preview && (
                      <IconButton 
                        color="error" 
                        onClick={handleClearImage}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    )}
                    
                    {errors.image && (
                      <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                        {errors.image}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('menu.variationsOptional')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                {/* ✅ ENHANCED: Better error display for variations */}
                {errors.variations && (
                  <Box sx={{ 
                    bgcolor: 'error.light', 
                    color: 'error.main', 
                    p: 1.5, 
                    borderRadius: 1,
                    mb: 2
                  }}>
                    <Typography variant="body2">{errors.variations}</Typography>
                  </Box>
                )}
                
                {errors.variation && (
                  <Box sx={{ 
                    bgcolor: 'warning.light', 
                    color: 'warning.main', 
                    p: 1.5, 
                    borderRadius: 1,
                    mb: 2
                  }}>
                    <Typography variant="body2">{errors.variation}</Typography>
                  </Box>
                )}
                
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label={t('menu.variationName')}
                      name="name"
                      value={newVariation.name}
                      onChange={handleVariationChange}
                      fullWidth
                      placeholder={t('menu.variationPlaceholder')}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label={t('menu.price')}
                      name="price"
                      type="number"
                      value={newVariation.price}
                      onChange={handleVariationChange}
                      fullWidth
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoney />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={2}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<Add />}
                        onClick={handleAddVariation}
                        disabled={!newVariation.name || !newVariation.price}
                        sx={{ 
                          height: '40px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                          }
                        }}
                      >
                        {editingVariation ? 'Update' : t('common.add')}
                      </Button>
                      
                      {editingVariation && (
                        <Button
                          variant="outlined"
                          color="inherit"
                          fullWidth
                          onClick={handleCancelEditVariation}
                          sx={{ height: '32px', fontSize: '0.8rem' }}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2 }}>
                  {formData.variations.length === 0 ? (
                    <Typography color="text.secondary" align="center">
                      {t('menu.noVariations')}
                    </Typography>
                  ) : (
                    <Grid container spacing={1}>
                      {formData.variations.map(variation => {
                        // Use unique key that works for both database and temporary variations
                        const uniqueKey = variation.variation_id || variation.id;
                        
                        return (
                          <Grid item key={uniqueKey} xs={12} sm={6}>
                            <Paper
                              elevation={1}
                              sx={{
                                p: 1.5,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {variation.name}
                                </Typography>
                                <Typography variant="caption" color="primary">
                                  +${parseFloat(variation.price || 0).toFixed(2)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    console.log('Edit button clicked for variation:', uniqueKey, variation);
                                    handleEditVariation(uniqueKey);
                                  }}
                                  color="primary"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    console.log('Delete button clicked for variation:', uniqueKey, variation);
                                    handleRemoveVariation(uniqueKey);
                                  }}
                                  color="error"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                {t('menu.preview')}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                {preview ? (
                  <Box
                    component="img"
                    src={preview}
                    alt={t('menu.itemPreviewAlt')}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 2,
                      mb: 2
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'action.hover',
                      borderRadius: 2,
                      mb: 2
                    }}
                  >
                    <Typography color="text.secondary">
                      {t('menu.noImageUploaded')}
                    </Typography>
                  </Box>
                )}
                
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formData.name || t('menu.itemName')}
                </Typography>
                
                <Typography variant="body1" color="primary.main" sx={{ fontWeight: 500, my: 1 }}>
                  ${formData.price ? parseFloat(formData.price).toFixed(2) : '0.00'}
                </Typography>
                
                <Chip 
                  label={categories.find(c => c.category_id.toString() === formData.categoryId)?.name || t('menu.category')}
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {formData.description || t('menu.itemDescriptionPlaceholder')}
                </Typography>
                
                {formData.variations.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('menu.variations')}:
                    </Typography>
                    
                    {formData.variations.map(variation => (
                      <Box key={variation.id || variation.variation_id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{variation.name}</Typography>
                        <Typography variant="body2">${parseFloat(variation.price || 0).toFixed(2)}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={onClose} 
            color="inherit" 
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
              }
            }}
          >
            {isLoading ? t('menu.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Dialog */}
      <SuccessDialog 
        open={successDialogOpen} 
        onClose={handleSuccessDialogClose} 
        message={t('menu.updateSuccess')}
      />
    </>
  );
};

export default EditMenu;